import React from 'react';
import { Info, Percent, Calculator, FileText, AlertTriangle, HelpCircle, BookOpen, Globe } from 'lucide-react';

/**
 * Компонент с информацией о НДС и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о налоге на добавленную стоимость (НДС)
      </h2>
      
      <div className="prose prose-green dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-green-500" />
          Что такое НДС?
        </h3>
        <p>
          <strong>Налог на добавленную стоимость (НДС)</strong> — это косвенный налог, который взимается с товаров и услуг на каждом этапе их производства и реализации. НДС включается в цену товара или услуги и оплачивается конечным потребителем. При этом компании-плательщики НДС имеют право вычитать НДС, уплаченный поставщикам, из суммы налога, подлежащей уплате в бюджет.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-green-500" />
          Как рассчитывается НДС?
        </h3>
        <p>
          Существует два основных метода расчета НДС:
        </p>
        <ol>
          <li>
            <strong>Начисление НДС</strong> (когда НДС добавляется к цене): сумма без НДС × (1 + ставка НДС ÷ 100)
            <br/>
            <em>Пример:</em> Товар стоит 1000 рублей без НДС, ставка 20%: 1000 × 1.2 = 1200 рублей (с НДС).
          </li>
          <li>
            <strong>Выделение НДС</strong> (когда НДС уже включен в цену): сумма с НДС ÷ (1 + ставка НДС ÷ 100)
            <br/>
            <em>Пример:</em> Товар стоит 1200 рублей с НДС, ставка 20%: 1200 ÷ 1.2 = 1000 рублей (без НДС).
          </li>
        </ol>
        <p>
          Сумма НДС может быть рассчитана как разница между итоговой суммой с НДС и суммой без НДС.
        </p>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2 mb-2">
            <Calculator className="w-4 h-4" /> Формулы расчета НДС
          </h4>
          <ul className="text-green-600 dark:text-green-400 text-sm mb-0 list-disc pl-5">
            <li>Сумма с НДС = Сумма без НДС × (1 + Ставка НДС/100)</li>
            <li>Сумма без НДС = Сумма с НДС ÷ (1 + Ставка НДС/100)</li>
            <li>Сумма НДС = Сумма с НДС - Сумма без НДС</li>
            <li>Сумма НДС = Сумма без НДС × (Ставка НДС/100)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-500" />
          Ставки НДС в России
        </h3>
        <p>
          В Российской Федерации действуют следующие ставки НДС:
        </p>
        <ul>
          <li>
            <strong>20%</strong> — основная ставка, применяется к большинству товаров и услуг.
          </li>
          <li>
            <strong>10%</strong> — льготная ставка, применяется к некоторым продовольственным товарам, товарам для детей, периодическим печатным изданиям, книжной продукции, лекарственным средствам и медицинским изделиям.
          </li>
          <li>
            <strong>0%</strong> — нулевая ставка, применяется к экспортируемым товарам, международным перевозкам и некоторым другим операциям. Даёт право на возмещение входного НДС.
          </li>
          <li>
            <strong>Без НДС</strong> — освобождение от уплаты НДС (не путать с нулевой ставкой). Применяется к определенным категориям товаров и услуг, а также к организациям, применяющим специальные налоговые режимы.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" /> Важные моменты при работе с НДС
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0 list-disc pl-5">
            <li>Плательщики НДС обязаны выставлять счета-фактуры с выделенной суммой НДС</li>
            <li>Право на вычет НДС возникает только при наличии правильно оформленного счета-фактуры</li>
            <li>При экспорте для подтверждения ставки 0% необходимо собрать пакет документов</li>
            <li>Организации на УСН, ЕНВД и ПСН не являются плательщиками НДС (за некоторыми исключениями)</li>
            <li>Подача декларации по НДС осуществляется ежеквартально</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-green-500" />
          Налоговый вычет по НДС
        </h3>
        <p>
          Налоговый вычет по НДС — это сумма налога, предъявленная налогоплательщику при приобретении товаров (работ, услуг), которую он вправе вычесть из суммы налога, подлежащей уплате в бюджет. Для применения вычета необходимо:
        </p>
        <ul>
          <li>Быть плательщиком НДС</li>
          <li>Приобретенные товары (работы, услуги) должны использоваться в деятельности, облагаемой НДС</li>
          <li>Иметь правильно оформленный счет-фактуру (или универсальный передаточный документ)</li>
          <li>Товары (работы, услуги) должны быть приняты к учету</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-green-500" />
          Часто задаваемые вопросы по НДС
        </h3>
        
        <h4 className="font-medium">Кто обязан платить НДС?</h4>
        <p>
          Плательщиками НДС являются организации и индивидуальные предприниматели на общей системе налогообложения. Также НДС уплачивается при импорте товаров на территорию РФ.
        </p>
        
        <h4 className="font-medium">Можно ли добровольно стать плательщиком НДС?</h4>
        <p>
          Да, организации и ИП, применяющие специальные налоговые режимы и освобожденные от уплаты НДС, могут добровольно зарегистрироваться в качестве плательщика НДС.
        </p>
        
        <h4 className="font-medium">Как получить освобождение от уплаты НДС?</h4>
        <p>
          Организации и ИП на общей системе налогообложения могут получить освобождение от уплаты НДС на 12 месяцев, если за предшествующие 3 месяца сумма выручки без учета НДС не превысила 2 млн рублей.
        </p>
        
        <h4 className="font-medium">Как учитывать НДС в цене товара?</h4>
        <p>
          При формировании цены товара или услуги НДС может быть включен в цену (цена с НДС) или указан отдельно сверх цены (цена без НДС + НДС). Для конечного потребителя обычно указывается цена с учетом НДС.
        </p>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-green-500" />
          НДС в разных странах
        </h3>
        <p>
          НДС применяется более чем в 160 странах мира, но ставки и особенности его исчисления различаются:
        </p>
        <ul>
          <li>В странах Европейского Союза ставки НДС варьируются от 17% до 27%</li>
          <li>В Великобритании основная ставка НДС составляет 20%</li>
          <li>В Швейцарии НДС равен 7,7%</li>
          <li>В Японии — 10%</li>
          <li>В США на федеральном уровне НДС отсутствует, вместо него применяется налог с продаж в отдельных штатах</li>
          <li>В Канаде действует Федеральный налог на товары и услуги (GST) со ставкой 5%</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор НДС поможет вам быстро и точно рассчитать сумму налога и правильно определить стоимость товаров и услуг как с учетом, так и без учета НДС!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;